<template>
    <Head title="External Requests" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div class="flex items-center gap-3">
                <div>
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">External Requests</h1>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Review and process requests submitted through the public portal</p>
                </div>
                <InfoButton title="External Requests Overview" variant="default">
                    <p>
                        <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                        The External Requests page displays all requests submitted through your organization's public portal.
                        External users (without login credentials) can submit incidents, work orders, and helpdesk tickets
                        through a unique portal URL. This page allows authorized staff to review these submissions and either
                        convert them to actionable items (incidents, work orders, or tickets) or close them without taking action.
                    </p>
                    <p>
                        <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                        • <strong>Search:</strong> Find requests by request number, title, submitter name, or email<br>
                        • <strong>Status Filter:</strong> Filter by Pending Review, Converted, or Closed Without Action<br>
                        • <strong>Request Type Filter:</strong> View only Incident, Work Order, or Helpdesk Ticket requests<br>
                        • <strong>Date Range:</strong> Filter requests by submission date<br>
                        • <strong>View Action:</strong> Click on any request to see full details and take action<br>
                        • <strong>Statistics Cards:</strong> Quick overview of pending, converted, closed, and total requests
                    </p>
                    <p>
                        <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                        • <strong>Color-coded badges</strong> identify request types: Red (Incident), Blue (Work Order), Green (Helpdesk Ticket)<br>
                        • <strong>Email matching</strong> automatically links requests to existing users when email addresses match<br>
                        • <strong>Pending requests</strong> require immediate attention - review and process them promptly<br>
                        • <strong>Converted requests</strong> are linked to their corresponding incident, work order, or ticket<br>
                        • When converting, submitter information is pre-filled if they're an existing user in your system
                    </p>
                </InfoButton>
            </div>
        </div>

        <!-- Statistics Cards -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div class="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Review</div>
                <div class="mt-2 text-3xl font-semibold text-blue-600 dark:text-blue-400">{{ statistics.pending }}</div>
            </div>
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div class="text-sm font-medium text-gray-500 dark:text-gray-400">Converted</div>
                <div class="mt-2 text-3xl font-semibold text-green-600 dark:text-green-400">{{ statistics.converted }}</div>
            </div>
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div class="text-sm font-medium text-gray-500 dark:text-gray-400">Closed</div>
                <div class="mt-2 text-3xl font-semibold text-gray-600 dark:text-gray-400">{{ statistics.closed }}</div>
            </div>
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div class="text-sm font-medium text-gray-500 dark:text-gray-400">Total</div>
                <div class="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">{{ statistics.total }}</div>
            </div>
        </div>

        <!-- Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-4 border border-gray-200 dark:border-gray-700">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Search -->
                <input
                    v-model="localFilters.search"
                    type="text"
                    placeholder="Search requests..."
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @input="debouncedSearch"
                />

                <!-- Status Filter -->
                <select
                    v-model="localFilters.status"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Status</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="converted">Converted</option>
                    <option value="closed_without_action">Closed Without Action</option>
                </select>

                <!-- Request Type Filter -->
                <select
                    v-model="localFilters.request_type"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Types</option>
                    <option value="incident">Incident</option>
                    <option value="work_order">Work Order</option>
                    <option value="helpdesk_ticket">Helpdesk Ticket</option>
                </select>

                <!-- Clear Filters Button -->
                <button
                    @click="clearFilters"
                    class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    Clear Filters
                </button>
            </div>

            <!-- Date Range Filters -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">From Date</label>
                    <input
                        v-model="localFilters.date_from"
                        type="date"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                        @change="applyFilters"
                    />
                </div>
                <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">To Date</label>
                    <input
                        v-model="localFilters.date_to"
                        type="date"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                        @change="applyFilters"
                    />
                </div>
            </div>
        </div>

        <!-- External Requests Table -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-700">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Request
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Request Type
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Submitter
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Converted To
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Submitted
                        </th>
                        <th scope="col" class="relative px-6 py-3">
                            <span class="sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr v-if="externalRequests.data.length === 0">
                        <td colspan="7" class="px-6 py-12 text-center">
                            <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No external requests found</h3>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">External requests will appear here when submitted through the public portal.</p>
                        </td>
                    </tr>
                    <tr v-for="request in externalRequests.data" :key="request.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm font-medium text-gray-900 dark:text-white">
                                {{ request.request_number }}
                            </div>
                            <div class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                {{ request.title }}
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span :class="getRequestTypeClass(request.request_type)">
                                {{ request.request_type_display }}
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <div v-if="request.submitter_name">{{ request.submitter_name }}</div>
                            <div v-else class="text-gray-400 dark:text-gray-500">Anonymous</div>
                            <div v-if="request.submitter_email" class="text-xs text-gray-500 dark:text-gray-400">
                                {{ request.submitter_email }}
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span :class="getStatusClass(request.status)">
                                {{ request.status_display }}
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span v-if="request.status === 'converted' && request.converted_type" :class="getConvertedTypeClass(request.converted_type)">
                                {{ getConvertedTypeDisplay(request.converted_type) }}
                            </span>
                            <span v-else class="text-xs text-gray-400 dark:text-gray-500">—</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div>{{ formatDate(request.created_at) }}</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link
                                :href="route('external-requests.show', request.id)"
                                class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                            >
                                View
                            </Link>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Pagination -->
            <div v-if="externalRequests.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{ externalRequests.from }}</span>
                            to
                            <span class="font-medium">{{ externalRequests.to }}</span>
                            of
                            <span class="font-medium">{{ externalRequests.total }}</span>
                            results
                        </p>
                    </div>
                    <div class="flex gap-2">
                        <template v-for="(link, index) in externalRequests.links" :key="index">
                            <Link
                                v-if="link.url"
                                :href="link.url"
                                :class="[
                                    'px-3 py-2 text-sm font-medium rounded-md',
                                    link.active
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                                ]"
                                v-html="link.label"
                            >
                            </Link>
                            <span
                                v-else
                                :class="[
                                    'px-3 py-2 text-sm font-medium rounded-md',
                                    'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                ]"
                                v-html="link.label"
                            >
                            </span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    externalRequests: Object,
    filters: Object,
    statistics: Object,
});

// Use timezone composable for date formatting
const { formatDateTime: formatDateTimeInTZ } = useTimezone();

const localFilters = ref({
    search: props.filters.search || '',
    status: props.filters.status || '',
    request_type: props.filters.request_type || '',
    date_from: props.filters.date_from || '',
    date_to: props.filters.date_to || '',
});

let debounceTimer = null;

const debouncedSearch = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        applyFilters();
    }, 500);
};

const applyFilters = () => {
    router.get(route('external-requests.index'), localFilters.value, {
        preserveState: true,
        preserveScroll: true,
    });
};

const clearFilters = () => {
    localFilters.value = {
        search: '',
        status: '',
        request_type: '',
        date_from: '',
        date_to: '',
    };
    applyFilters();
};

const getRequestTypeClass = (requestType) => {
    const classes = {
        incident: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        work_order: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        helpdesk_ticket: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    };
    return classes[requestType] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getStatusClass = (status) => {
    const classes = {
        pending_review: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        converted: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        closed_without_action: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    };
    return classes[status] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getConvertedTypeClass = (convertedType) => {
    const classes = {
        incident: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        work_order: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        helpdesk_ticket: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    };
    return classes[convertedType] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getConvertedTypeDisplay = (convertedType) => {
    const displays = {
        incident: 'Incident',
        work_order: 'Work Order',
        helpdesk_ticket: 'Ticket',
    };
    return displays[convertedType] || convertedType;
};

// Format date in company timezone
const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateTimeInTZ(date);
};
</script>
